import { PureTableRow } from '../../../common/PureTable/PureTable.types';
import styles from './DownloadsHistory.module.scss';
import Button from '../../../common/Button/Button';
import { useContentDownloadsList } from '../../../../lib/api/scormApi';
import { AddAction, Loader } from '../../../common/PureTable/PureTableActions';
import { Label } from '../../../common/Label/Label';
import { DOWNLOADS_HISTORY_LIST_LABELS } from '../../../../lib/constants/labelKeys';
import WithDevice from '../../../common/WithDevice/WithDevice';
import { ItemActionProps } from '../../../../lib/context/ScormContext';
import { Api } from '../../../../lib/types/apiSpec';

type TableItemProps = { id: string; trainingId: string; type: Api.TrainingType | null; name: string };
type ItemActionWithNullProps = Omit<ItemActionProps, 'trainingType'> & { trainingType: Api.TrainingType | null };

const InlineAction = <T extends TableItemProps>({
  isOnDownloadsList,
  data,
  isLoading,
  onClickHandler,
}: {
  isOnDownloadsList: boolean;
  isLoading: boolean;
  data: T;
  onClickHandler: (props: ItemActionWithNullProps) => void;
}) => {
  if (isLoading) {
    return <Loader className={styles.inlineActionLoader} />;
  }
  const { trainingId, name, type } = data;

  return isOnDownloadsList ? (
    <Label name={DOWNLOADS_HISTORY_LIST_LABELS.alreadyOnList} className={styles.inlineAction} />
  ) : (
    <>
      <WithDevice tablet mobile>
        <AddAction
          id={`add-downloads-list-action-${data.id}`}
          titleKey="add.downloadsListItem"
          className={styles.inlineAction}
          onClick={() => onClickHandler({ id: trainingId, trainingName: name, trainingType: type })}
        />
      </WithDevice>
      <WithDevice desktop>
        <Button
          variant="text"
          onClick={() => onClickHandler({ id: trainingId, trainingName: name, trainingType: type })}
          className={styles.inlineAction}
        >
          <Label name={DOWNLOADS_HISTORY_LIST_LABELS.addToListButton} />
        </Button>
      </WithDevice>
    </>
  );
};

export const mapDownloadItemToTableRow =
  <T extends TableItemProps>(
    props: (keyof T)[],
    {
      onClickHandler,
      downloadsList,
      actionsState,
    }: {
      onClickHandler: (props: ItemActionWithNullProps) => void;
      downloadsList: ReturnType<typeof useContentDownloadsList>;
      actionsState: Record<string, boolean>;
    },
  ) =>
  (acc: Record<string, PureTableRow>, curr: T): Record<string, PureTableRow> => {
    const isOnDownloadsList = !!downloadsList.data?.find(item => item.trainingId === curr.trainingId);

    return {
      ...acc,
      [curr.trainingId]: [
        <span key={`${curr.trainingId}|${curr[props[0]]}`} className={styles.tableRow}>
          <span>{props.map(prop => curr[prop] as string).join()}</span>
          <div className={styles.inlineActionWrapper}>
            <InlineAction
              isLoading={actionsState[curr.trainingId]}
              isOnDownloadsList={isOnDownloadsList}
              data={curr}
              onClickHandler={onClickHandler}
            />
          </div>
        </span>,
      ],
    };
  };
